import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import HCLogo from '../components/HCLogo';
import AppSwitcher from '../components/AppSwitcher';
import LanguageSelector from '../components/LanguageSelector';
import ProductionDashboard, { ComparisonView } from '../components/production/ProductionDashboard';
import OperatorsManager from '../components/production/OperatorsManager';
import WorkTypesManager from '../components/production/WorkTypesManager';
import EntriesManager from '../components/production/EntriesManager';
import OperatorHistory from '../components/production/OperatorHistory';
import CustomersManager from '../components/production/CustomersManager';
import {
  BarChart3, Plus, Users as UsersIcon, Tag, LineChart, LogOut, Factory, Building2
} from 'lucide-react';

const ProductionDashboardPage = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState('dashboard');
  const [historyOperator, setHistoryOperator] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-14 border-b border-slate-200 bg-white px-4 lg:px-8 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2.5">
          <HCLogo size="md" />
          <div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">{t('production.dashboard.title')}</div>
            <div className="text-[11px] text-slate-500 leading-tight">{t('production.dashboard.subtitle')}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector size="sm" />
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center text-xs font-semibold">
              {user?.initials}
            </div>
            <div className="text-xs">
              <div className="font-medium text-slate-900">{user?.name}</div>
              <div className="text-slate-500 flex items-center gap-1"><Factory className="w-3 h-3" />{t('login.role.production')}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-1.5" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> {t('common.logout')}
          </Button>
        </div>
      </header>

      <AppSwitcher />

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-slate-900">{t('production.dashboard.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('production.dashboard.subtitle')}</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto mb-4">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4" /> {t('common.dashboard')}
            </TabsTrigger>
            <TabsTrigger value="entries" className="gap-2" data-testid="tab-entries">
              <Plus className="w-4 h-4" /> {t('production.entries.title')}
            </TabsTrigger>
            <TabsTrigger value="customers" className="gap-2" data-testid="tab-customers">
              <Building2 className="w-4 h-4" /> {t('production.customers.title')}
            </TabsTrigger>
            <TabsTrigger value="worktypes" className="gap-2" data-testid="tab-worktypes">
              <Tag className="w-4 h-4" /> {t('production.workTypes.title')}
            </TabsTrigger>
            <TabsTrigger value="operators" className="gap-2" data-testid="tab-operators">
              <UsersIcon className="w-4 h-4" /> {t('production.operators.title')}
            </TabsTrigger>
            <TabsTrigger value="comparison" className="gap-2" data-testid="tab-comparison">
              <LineChart className="w-4 h-4" /> {t('common.reports')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-0">
            <ProductionDashboard onOpenOperator={setHistoryOperator} />
          </TabsContent>
          <TabsContent value="entries" className="mt-0">
            <EntriesManager />
          </TabsContent>
          <TabsContent value="customers" className="mt-0">
            <CustomersManager />
          </TabsContent>
          <TabsContent value="worktypes" className="mt-0">
            <WorkTypesManager />
          </TabsContent>
          <TabsContent value="operators" className="mt-0">
            <OperatorsManager onView={setHistoryOperator} />
          </TabsContent>
          <TabsContent value="comparison" className="mt-0">
            <ComparisonView />
          </TabsContent>
        </Tabs>

        <OperatorHistory operator={historyOperator} onClose={() => setHistoryOperator(null)} />
      </main>
    </div>
  );
};

export default ProductionDashboardPage;
